import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  inject,
  Component,
  OnInit,
  OnDestroy,
  HostListener
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormControl,
  AbstractControl
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  map
} from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../../../kuba.services';

import { AppraisalTemplateService } from '../../templates/services';
import {
  AppraisalTemplateCreateDto,
  AppraisalTemplateUpdateDto,
  AppraisalTemplateDto,
  AppraisalTemplateListItemDto,
  AppraisalTemplateType,
  QuestionType,
  AppraisalTemplateSectionCreateDto,
  AppraisalTemplateQuestionCreateDto,
  AppraisalTemplateSectionDto,
  AppraisalTemplateQuestionDto
} from '../../templates/models';
import { AppraisalTemplatePaginatedFilterDto } from '../../templates/models/appraisal-template-paginated-filter.dto';

// Private types
type TemplateForm = FormGroup<{
  name: FormControl<string>;
  description: FormControl<string>;
  templateType: FormControl<AppraisalTemplateType>;
  requiresBothPartiesSignature: FormControl<boolean>;
  version: FormControl<number>;
}>;

type SectionForm = FormGroup<{
  title: FormControl<string>;
  description: FormControl<string>;
  sortOrder: FormControl<number>;
  isRequired: FormControl<boolean>;
  isExpandedByDefault: FormControl<boolean>;
  questions: FormArray<QuestionForm>;
}>;

type QuestionForm = FormGroup<{
  questionText: FormControl<string>;
  questionType: FormControl<QuestionType>;
  sortOrder: FormControl<number>;
  isRequired: FormControl<boolean>;
  isEmployeeVisible: FormControl<boolean>;
  isManagerVisible: FormControl<boolean>;
  employeeCanAnswer: FormControl<boolean>;
  managerCanAnswer: FormControl<boolean>;
  likertLowLabel: FormControl<string>;
  likertHighLabel: FormControl<string>;
  placeholder: FormControl<string>;
  maxLength: FormControl<number | null>;
  textAreaRows: FormControl<number>;
}>;

type TemplateVersionOption = {
  templateId: number;
  version: number;
  name: string;
  label: string;
};

@Component({
  selector: 'template-builder',
  templateUrl: './template-builder.component.html',
  styleUrls: ['./template-builder.component.scss'],
  standalone: false
})
export class TemplateBuilderComponent implements OnInit, OnDestroy {
  //#region Variables
  private readonly notifications = inject(NotificationService);

  // Wizard
  activeStep: number = 0;
  steps: any[] = [
    { label: 'TEMPLATE_METADATA', icon: 'icon-info' },
    { label: 'SECTIONS', icon: 'icon-list' },
    { label: 'QUESTIONS', icon: 'icon-question' }
  ];

  // Forms
  templateForm: TemplateForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    description: new FormControl('', Validators.maxLength(500)),
    templateType: new FormControl<AppraisalTemplateType>(
      AppraisalTemplateType.Standard,
      Validators.required
    ),
    requiresBothPartiesSignature: new FormControl(false),
    version: new FormControl(1, [Validators.required, Validators.min(1)])
  });

  sectionsForm: FormGroup<{
    sections: FormArray<SectionForm>;
  }>;
  questionsForm: FormGroup<{
    questions: FormArray<QuestionForm>;
  }>;

  // Data
  templateId: number | null = null;
  template: AppraisalTemplateDto | null = null;
  isEditMode: boolean = false;
  isLoading: boolean = false;
  isSaving: boolean = false;
  versionOptions: TemplateVersionOption[] = [];
  selectedVersionTemplateId: number | null = null;
  suggestedVersion: number | null = null;

  // Enums
  questionTypeOptions: any[] = [];

  // Current section for questions step
  currentSectionIndex: number = 0;

  // Auto-save
  private autoSaveSubject = new Subject<void>();
  hasUnsavedChanges: boolean = false;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Local storage key
  private readonly AUTOSAVE_KEY = 'appraisal_template_autosave';
  //#endregion

  //#region Constructor
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private templateService: AppraisalTemplateService
  ) {
    this.initializeEnums();
    this.initializeForms();
    this.setupAutoSave();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    // Check if editing existing template
    this.subscriptions.push(
      this.route.params.subscribe(params => {
        if (params['id']) {
          this.templateId = +params['id'];
          this.isEditMode = true;
          this.loadTemplate(this.templateId);
        } else {
          // Try to restore from autosave
          this.restoreFromAutoSave();
        }
      })
    );

    // Listen to form changes
    this.subscriptions.push(
      this.templateForm.valueChanges.subscribe(() => {
        this.hasUnsavedChanges = true;
        this.autoSaveSubject.next();
      })
    );

    this.subscriptions.push(
      this.sectionsForm.valueChanges.subscribe(() => {
        this.hasUnsavedChanges = true;
        this.autoSaveSubject.next();
      })
    );

    this.subscriptions.push(
      this.questionsForm.valueChanges.subscribe(() => {
        this.hasUnsavedChanges = true;
        this.autoSaveSubject.next();
      })
    );

    this.setupChangeListeners();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasUnsavedChanges) {
      $event.returnValue = true;
    }
  }
  //#endregion

  //#region Initialization
  private initializeEnums(): void {
    this.questionTypeOptions = [
      { labelKey: 'LIKERT_5_SCALE', value: QuestionType.Likert5 },
      { labelKey: 'FREE_TEXT', value: QuestionType.FreeText },
      { labelKey: 'YES_NO', value: QuestionType.YesNo }
    ];
  }

  private initializeForms(): void {
    // Step 1: Sections
    this.sectionsForm = this.fb.group({
      sections: this.fb.array<SectionForm>([])
    });
    // Step 2: Questions (will be populated based on current section)
    this.questionsForm = this.fb.group({
      questions: this.fb.array<QuestionForm>([])
    });
  }

  private setupAutoSave(): void {
    this.subscriptions.push(
      this.autoSaveSubject
        .pipe(
          debounceTime(2000) // Auto-save 2 seconds after last change
        )
        .subscribe(() => {
          this.saveToLocalStorage();
        })
    );
  }

  private setupChangeListeners(): void {
    const nameControl = this.templateForm.get('name');
    const versionControl = this.templateForm.get('version');

    const name$ = nameControl.valueChanges.pipe(startWith(nameControl.value));
    const version$ = versionControl.valueChanges.pipe(
      startWith(versionControl.value)
    );

    this.subscriptions.push(
      combineLatest([name$, version$])
        .pipe(
          debounceTime(800),
          map(([name, version]) => ({
            name: (name ?? '').toString(),
            version: version ?? 1
          })),
          distinctUntilChanged(
            (prev, curr) =>
              prev.name === curr.name && prev.version === curr.version
          ),
          filter(x => x.name.trim() !== '')
        )
        .subscribe(({ name, version }) =>
          this.checkNameAvailability(name, version)
        )
    );
  }

  //#endregion

  //#region Load Template
  private loadTemplate(id: number): void {
    this.isLoading = true;

    this.templateService.getById(id).subscribe({
      next: template => {
        this.template = template;
        this.selectedVersionTemplateId = template.Id;
        this.populateForms(template);
        this.loadTemplateVersions(template);
        this.isLoading = false;
        // Clear autosave when loading existing template
        this.clearAutoSave();
      },
      error: () => {
        this.showError(this.translate.instant('ERROR_LOADING_TEMPLATE'));
        this.isLoading = false;
        this.navigateToTemplatesRoot();
      }
    });
  }

  private populateForms(template: AppraisalTemplateDto): void {
    // Populate template metadata
    this.templateForm.patchValue({
      name: template.Name,
      description: template.Description,
      templateType: AppraisalTemplateType.Standard,
      requiresBothPartiesSignature: !!template.RequiresBothPartiesSignature,
      version: template.Version
    }, { emitEvent: false });

    // Populate sections
    const sectionsArray = this.getSectionsArray();
    sectionsArray.clear({ emitEvent: false });
    template.Sections.forEach(section => {
      sectionsArray.push(this.createSectionFormGroup(section));
    });

    this.templateForm.markAsPristine();
    this.templateForm.markAsUntouched();
    this.sectionsForm.markAsPristine();
    this.sectionsForm.markAsUntouched();
    this.questionsForm.markAsPristine();
    this.questionsForm.markAsUntouched();
    this.hasUnsavedChanges = false;
  }

  private loadTemplateVersions(template: AppraisalTemplateDto): void {
    const filters: AppraisalTemplatePaginatedFilterDto = {
      pageNumber: 1,
      pageSize: 1000,
      sortColumn: 'CreatedOn',
      sortDirection: 'DESC'
    };

    this.templateService.getPagedList(BaseServices.BusinessId, filters).subscribe({
      next: response => {
        const candidates = (response.Items || []).filter(
          item => this.belongsToTemplateFamily(item, template)
        );

        const currentIncluded = candidates.some(item => item.Id === template.Id);
        if (!currentIncluded) {
          candidates.push({
            Id: template.Id,
            TemplateFamilyId: template.TemplateFamilyId,
            Name: template.Name,
            Description: template.Description,
            TemplateType: Number(template.TemplateType),
            IsActive: template.IsActive,
            RequiresBothPartiesSignature: !!template.RequiresBothPartiesSignature,
            Version: template.Version,
            HasMeetings: template.HasMeetings,
            CreatedBy: template.CreatedByCreatedOn,
            ModifiedByModifiedOn: template.ModifiedByModifiedOn,
            AssignedDepartments: template.AssignedDepartments
          });
        }

        this.versionOptions = candidates
          .map(item => ({
            templateId: item.Id,
            version: item.Version,
            name: item.Name,
            label: `v${item.Version} - ${item.Name}`
          }))
          .sort((a, b) => b.version - a.version);

        this.applySuggestedVersion(template);
      },
      error: () => {
        this.versionOptions = [
          {
            templateId: template.Id,
            version: template.Version,
            name: template.Name,
            label: `v${template.Version} - ${template.Name}`
          }
        ];
        this.applySuggestedVersion(template);
      }
    });
  }

  private applySuggestedVersion(template: AppraisalTemplateDto): void {
    if (!this.isEditMode) {
      this.suggestedVersion = null;
      return;
    }

    const maxVersion =
      this.versionOptions.length > 0
        ? Math.max(...this.versionOptions.map(option => option.version))
        : template.Version;
    this.suggestedVersion = maxVersion + 1;
  }

  private normalizeName(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private belongsToTemplateFamily(
    item: AppraisalTemplateListItemDto,
    template: AppraisalTemplateDto
  ): boolean {
    if (template.TemplateFamilyId && item.TemplateFamilyId) {
      return item.TemplateFamilyId === template.TemplateFamilyId;
    }

    return this.normalizeName(item.Name) === this.normalizeName(template.Name);
  }
  //#endregion

  //#region Form Getters
  get sectionsArray() {
    return this.sectionsForm.controls.sections;
  }

  get questionsArray() {
    return this.questionsForm.controls.questions;
  }

  private getSectionsArray() {
    return this.sectionsForm.controls.sections;
  }
  //#endregion

  //#region Name Availability Check

  /**
   * Checks if a template name is available.
   * @param name The name to check.
   */
  checkNameAvailability(name: string, version: number): void {
    const nameControl = this.templateForm.get('name');
    const versionControl = this.templateForm.get('version');
    const controls = [nameControl, versionControl];

    if (this.isEditMode) {
      this.resetNameAvailability(controls);
      return;
    }

    if (name && name.trim() !== '' && version > 0) {
      this.templateService
        .checkNameAvailability(
          BaseServices.BusinessId,
          name,
          version
        )
        .subscribe({
          next: isAvailable => {
            if (!isAvailable) {
              nameControl?.setErrors({
                ...nameControl?.errors,
                nameTaken: true
              });
              versionControl?.setErrors({
                ...versionControl?.errors,
                nameTaken: true
              });
            } else {
              this.resetNameAvailability(controls);
            }
          },
          error: () => {
            this.resetNameAvailability(controls);
          }
        });
    } else {
      this.resetNameAvailability(controls);
    }
  }

  /**
   * Resets the name availability error on the form controls.
   * @param controls The form controls to reset.
   */
  private resetNameAvailability(controls: AbstractControl[]): void {
    controls.forEach(control => {
      if (control?.hasError('nameTaken')) {
        const errors = { ...control?.errors };
        delete errors['nameTaken'];
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    });
  }

  //#endregion

  onVersionSourceChange(event: Event): void {
    const selectedTemplateId = Number(
      (event.target as HTMLSelectElement).value ?? 0
    );
    if (!selectedTemplateId || selectedTemplateId === this.templateId) {
      return;
    }

    const switchVersion = () => {
      const targetRoute = this.route.parent ?? this.route;
      this.router.navigate(['template/edit', selectedTemplateId], {
        relativeTo: targetRoute
      });
    };

    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message: this.translate.instant('UNSAVED_CHANGES_WARNING'),
        accept: switchVersion,
        reject: () => {
          this.selectedVersionTemplateId = this.templateId;
        }
      });
      return;
    }

    switchVersion();
  }

  //#region Section Management
  createSectionFormGroup(section?: AppraisalTemplateSectionDto): SectionForm {
    const sectionData: any = section ?? {};
    const sortOrder = Number(section?.SortOrder ?? sectionData.sortOrder ?? this.sectionsArray.length);
    const title = String(section?.Title ?? sectionData.title ?? '');
    const description = String(section?.Description ?? sectionData.description ?? '');
    const questions: any[] = section?.Questions ?? sectionData.questions ?? [];

    const sectionGroup = this.fb.group({
      title: [
        title,
        [Validators.required, Validators.maxLength(200)]
      ],
      description: [description, Validators.maxLength(500)],
      sortOrder: [sortOrder],
      isRequired: [false],
      isExpandedByDefault: [true],
      questions: this.fb.array(
        questions.map((q: any) => this.createQuestionFormGroup(q))
      )
    });

    return sectionGroup;
  }

  addSection(): void {
    const sectionsArray = this.getSectionsArray();
    sectionsArray.push(this.createSectionFormGroup());
    this.hasUnsavedChanges = true;
    this.autoSaveSubject.next();
  }

  removeSection(index: number): void {
    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_DELETE_SECTION'),
      accept: () => {
        this.getSectionsArray().removeAt(index);
        this.hasUnsavedChanges = true;
        this.autoSaveSubject.next();
      }
    });
  }

  dropSection(event: CdkDragDrop<any[]>): void {
    const sectionsControls = this.getSectionsArray().controls;
    moveItemInArray(sectionsControls, event.previousIndex, event.currentIndex);

    sectionsControls.forEach((sectionControl, index) => {
      sectionControl.controls.sortOrder.setValue(index, { emitEvent: false });
    });
    this.getSectionsArray().updateValueAndValidity({ emitEvent: false });

    this.hasUnsavedChanges = true;
    this.autoSaveSubject.next();
  }
  //#endregion

  //#region Question Management
  createQuestionFormGroup(
    question?: AppraisalTemplateQuestionDto
  ): QuestionForm {
    const questionData: any = question ?? {};
    const sortOrder = Number(question?.SortOrder ?? questionData.sortOrder ?? 0);

    const questionText = String(question?.QuestionText ?? questionData.questionText ?? '');
    const questionType = Number(
      question?.QuestionType ?? questionData.questionType ?? QuestionType.FreeText
    ) as QuestionType;
    const isRequired = !!(question?.IsRequired ?? questionData.isRequired ?? false);
    const isEmployeeVisible = !!(question?.IsEmployeeVisible ?? questionData.isEmployeeVisible ?? true);
    const isManagerVisible = !!(question?.IsManagerVisible ?? questionData.isManagerVisible ?? true);
    const employeeCanAnswer = !!(question?.EmployeeCanAnswer ?? questionData.employeeCanAnswer ?? isEmployeeVisible);
    const managerCanAnswer = !!(question?.ManagerCanAnswer ?? questionData.managerCanAnswer ?? isManagerVisible);
    const likertLowLabel = String(question?.LikertLowLabel ?? questionData.likertLowLabel ?? '');
    const likertHighLabel = String(question?.LikertHighLabel ?? questionData.likertHighLabel ?? '');
    const placeholder = String(question?.Placeholder ?? questionData.placeholder ?? '');
    const rawMaxLength = question?.MaxLength ?? questionData.maxLength ?? null;
    const maxLength = rawMaxLength === null || rawMaxLength === ''
      ? null
      : Number(rawMaxLength);
    const textAreaRows = Number(question?.TextAreaRows ?? questionData.textAreaRows ?? 4);

    return this.fb.group({
      questionText: [
        questionText,
        [Validators.required, Validators.maxLength(500)]
      ],
      questionType: [
        questionType,
        Validators.required
      ],
      sortOrder: [sortOrder],
      isRequired: [isRequired],
      isEmployeeVisible: [isEmployeeVisible],
      isManagerVisible: [isManagerVisible],
      employeeCanAnswer: [employeeCanAnswer && isEmployeeVisible],
      managerCanAnswer: [managerCanAnswer && isManagerVisible],
      likertLowLabel: [likertLowLabel, Validators.maxLength(50)],
      likertHighLabel: [likertHighLabel, Validators.maxLength(50)],
      placeholder: [placeholder, Validators.maxLength(200)],
      maxLength: [maxLength, Validators.min(1)],
      textAreaRows: [
        textAreaRows,
        [Validators.required, Validators.min(1), Validators.max(20)]
      ]
    });
  }

  getQuestionsForCurrentSection(): FormArray<QuestionForm> {
    const section = this.sectionsArray.at(
      this.currentSectionIndex
    ) as SectionForm;
    return section.controls.questions;
  }

  addQuestion(): void {
    const questionsArray = this.getQuestionsForCurrentSection();
    questionsArray.push(this.createQuestionFormGroup());
    this.hasUnsavedChanges = true;
    this.autoSaveSubject.next();
  }

  removeQuestion(index: number): void {
    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_DELETE_QUESTION'),
      accept: () => {
        this.getQuestionsForCurrentSection().removeAt(index);
        this.hasUnsavedChanges = true;
        this.autoSaveSubject.next();
      }
    });
  }

  dropQuestion(event: CdkDragDrop<any[]>): void {
    const questionsArray = this.getQuestionsForCurrentSection();
    const questionControls = questionsArray.controls;

    moveItemInArray(questionControls, event.previousIndex, event.currentIndex);

    questionControls.forEach((questionControl, index) => {
      questionControl.controls.sortOrder.setValue(index, { emitEvent: false });
    });
    questionsArray.updateValueAndValidity({ emitEvent: false });

    this.hasUnsavedChanges = true;
    this.autoSaveSubject.next();
  }

  selectSection(index: number): void {
    this.currentSectionIndex = index;
  }

  onQuestionVisibilityChanged(
    questionGroup: QuestionForm,
    role: 'employee' | 'manager'
  ): void {
    const visibleControl = role === 'employee'
      ? questionGroup.controls.isEmployeeVisible
      : questionGroup.controls.isManagerVisible;
    const canAnswerControl = role === 'employee'
      ? questionGroup.controls.employeeCanAnswer
      : questionGroup.controls.managerCanAnswer;

    if (!visibleControl.value) {
      canAnswerControl.setValue(false);
    }
  }

  hasQuestionVisibleParticipant(questionGroup: QuestionForm): boolean {
    return ( questionGroup.controls.isEmployeeVisible.value ||
      questionGroup.controls.isManagerVisible.value
    );
  }

  hasQuestionAnswerer(questionGroup: QuestionForm): boolean {
    return ( (
      questionGroup.controls.isEmployeeVisible.value &&
      questionGroup.controls.employeeCanAnswer.value
    ) || (
      questionGroup.controls.isManagerVisible.value &&
      questionGroup.controls.managerCanAnswer.value)
    );
  }
  //#endregion

  //#region Wizard Navigation
  nextStep(): void {
    if (this.activeStep === 0 && !this.templateForm.valid) {
      this.markFormGroupTouched(this.templateForm);
      this.showError(this.translate.instant('PLEASE_FIX_VALIDATION_ERRORS'));
      return;
    }

    if (this.activeStep === 1) {
      this.sectionsForm.markAllAsTouched();

      if (this.sectionsArray.length === 0) {
        this.showError(this.translate.instant('ADD_AT_LEAST_ONE_SECTION'));
        return;
      }

      if (!this.sectionsArray.valid) {
        this.showError(this.translate.instant('PLEASE_FIX_VALIDATION_ERRORS'));
        return;
      }
    }

    if (this.activeStep < this.steps.length - 1) {
      this.activeStep++;
    }
  }

  previousStep(): void {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  }

  goToStep(index: number): void {
    this.activeStep = index;
  }
  //#endregion

  //#region Save & Submit
  canSaveTemplate(): boolean {
    return ( this.templateForm.valid
      && this.sectionsArray.length > 0
      && this.sectionsArray.valid
      && !this.hasInvalidQuestionPermissions()
      && !this.isSaving
    );
  }

  saveTemplate(): void {
    // Mark all as touched to show errors
    this.templateForm.markAllAsTouched();
    this.sectionsForm.markAllAsTouched();

    // Validate all forms
    if (!this.templateForm.valid) {
      this.showError(this.translate.instant('PLEASE_FIX_VALIDATION_ERRORS'));
      this.activeStep = 0;
      return;
    }

    if (this.sectionsArray.length === 0) {
      this.showError(this.translate.instant('ADD_AT_LEAST_ONE_SECTION'));
      this.activeStep = 1;
      return;
    }

    if (!this.sectionsArray.valid) {
      this.showError(this.translate.instant('PLEASE_FIX_VALIDATION_ERRORS'));
      this.activeStep = this.hasInvalidSectionMetadata() ? 1 : 2;
      return;
    }

    if (this.hasInvalidQuestionPermissions()) {
      this.showError(this.translate.instant('PLEASE_FIX_VALIDATION_ERRORS'));
      this.activeStep = 2;
      return;
    }

    this.isSaving = true;

    const templateData = this.buildTemplateDto();

    if (this.isEditMode && this.templateId) {
      this.updateTemplate(templateData as AppraisalTemplateUpdateDto);
    } else {
      this.createTemplate(templateData as AppraisalTemplateCreateDto);
    }
  }

  private buildTemplateDto():
    | AppraisalTemplateCreateDto
    | AppraisalTemplateUpdateDto {
    const formValue = this.templateForm.value;
    const sections = this.sectionsArray.controls;

    const dto: any = {
      Name: formValue.name,
      Description: formValue.description,
      TemplateType: Number(AppraisalTemplateType.Standard),
      Version: this.isEditMode
        ? (formValue.version ?? this.template?.Version ?? 1)
        : 1,
      RequiresBothPartiesSignature: !!formValue.requiresBothPartiesSignature,
      Sections: sections.map(section => this.buildSectionCreateDto(section))
    };

    if (!this.isEditMode) {
      dto.BusinessId = BaseServices.BusinessId;
    }

    return dto;
  }

  private buildSectionCreateDto(
    sectionGroup: SectionForm
  ): AppraisalTemplateSectionCreateDto {
    const sectionValue = sectionGroup.value;
    const questions = sectionGroup.controls.questions.controls;

    return {
      Title: sectionValue.title,
      Description: sectionValue.description,
      SortOrder: sectionValue.sortOrder,
      Questions: questions.map(question =>
        this.buildQuestionCreateDto(question)
      ),
      IsRequired: false,
      IsExpandedByDefault: true
    };
  }

  private buildQuestionCreateDto(
    questionGroup: QuestionForm
  ): AppraisalTemplateQuestionCreateDto {
    const questionValue = questionGroup.getRawValue();
    const isEmployeeVisible = !!questionValue.isEmployeeVisible;
    const isManagerVisible = !!questionValue.isManagerVisible;

    return {
      QuestionText: questionValue.questionText,
      QuestionType: Number(questionValue.questionType),
      SortOrder: questionValue.sortOrder,
      IsRequired: questionValue.isRequired,
      IsEmployeeVisible: isEmployeeVisible,
      IsManagerVisible: isManagerVisible,
      EmployeeCanAnswer: isEmployeeVisible && !!questionValue.employeeCanAnswer,
      ManagerCanAnswer: isManagerVisible && !!questionValue.managerCanAnswer,
      LikertLowLabel: questionValue.likertLowLabel,
      LikertHighLabel: questionValue.likertHighLabel,
      Placeholder: questionValue.placeholder,
      MaxLength: questionValue.maxLength,
      TextAreaRows: questionValue.textAreaRows
    };
  }

  private createTemplate(dto: AppraisalTemplateCreateDto): void {
    this.subscriptions.push(
      this.templateService.create(dto).subscribe({
        next: template => {
          this.showSuccess(this.translate.instant('TEMPLATE_CREATED_SUCCESS'));
          this.hasUnsavedChanges = false;
          this.clearAutoSave();
          this.navigateToTemplatesRoot();
        },
        error: error => {
          console.error('Error creating form:', error);
          this.showError(
            error.message || this.translate.instant('TEMPLATE_CREATED_ERROR')
          );
          this.isSaving = false;
        }
      })
    );
  }

  private updateTemplate(dto: AppraisalTemplateUpdateDto): void {
    this.subscriptions.push(
      this.templateService.update(this.templateId!, dto).subscribe({
        next: template => {
          this.showSuccess(this.translate.instant('TEMPLATE_UPDATED_SUCCESS'));
          this.hasUnsavedChanges = false;
          this.clearAutoSave();
          this.navigateToTemplatesRoot();
        },
        error: error => {
          console.error('Error updating form:', error);
          this.showError(
            error.message || this.translate.instant('TEMPLATE_UPDATED_ERROR')
          );
          this.isSaving = false;
        }
      })
    );
  }

  cancel(): void {
    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message: this.translate.instant('UNSAVED_CHANGES_WARNING'),
        accept: () => {
          this.clearAutoSave();
          this.navigateToTemplatesRoot();
        }
      });
    } else {
      this.navigateToTemplatesRoot();
    }
  }
  //#endregion

  getButtonLabel(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return !translated || translated === key ? fallback : translated;
  }

  goToAppraisalsRoot(): void {
    const targetRoute = this.route.parent ?? this.route;
    this.router.navigate(['./'], {
      relativeTo: targetRoute,
      queryParams: { tab: 'list' }
    });
  }

  //#region Auto-save
  private saveToLocalStorage(): void {
    const data = {
      templateForm: this.templateForm.value,
      sections: this.sectionsArray.value,
      timestamp: new Date().toISOString()
    };

    try {
      localStorage.setItem(this.AUTOSAVE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private restoreFromAutoSave(): void {
    try {
      const saved = localStorage.getItem(this.AUTOSAVE_KEY);
      if (saved) {
        const data = JSON.parse(saved);

        this.confirmationService.confirm({
          message: this.translate.instant('RESTORE_AUTOSAVE_QUESTION'),
          accept: () => {
            this.templateForm.patchValue({
              ...data.templateForm,
              templateType: AppraisalTemplateType.Standard,
              version: 1
            });

            const sectionsArray = this.getSectionsArray();
            sectionsArray.clear();
            data.sections.forEach((section: any) => {
              sectionsArray.push(this.createSectionFormGroup(section));
            });

            this.hasUnsavedChanges = true;
          },
          reject: () => {
            this.clearAutoSave();
          }
        });
      }
    } catch (error) {
      console.error('Error restoring from localStorage:', error);
    }
  }

  private clearAutoSave(): void {
    try {
      localStorage.removeItem(this.AUTOSAVE_KEY);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  private navigateToTemplatesRoot(): void {
    const targetRoute = this.route.parent ?? this.route;
    this.router.navigate(['./'], {
      relativeTo: targetRoute,
      queryParams: { tab: 'templates' }
    });
  }
  //#endregion

  //#region Helper Methods
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private hasInvalidSectionMetadata(): boolean {
    return this.sectionsArray.controls.some(
      section =>
        section.controls.title.invalid ||
        section.controls.description.invalid
    );
  }

  private hasInvalidQuestionPermissions(): boolean {
    return this.sectionsArray.controls.some(section =>
      section.controls.questions.controls.some(question =>
        !this.hasQuestionVisibleParticipant(question) ||
        !this.hasQuestionAnswerer(question)
      )
    );
  }

  showSuccess(message: string): void {
    this.notifications.success('UI_NOTIFICATIONS.SAVED');
  }

  showError(message: string): void {
    this.notifications.success('UI_NOTIFICATIONS.DELETED');
  }

  getQuestionTypeLabel(type: QuestionType): string {
    const option = this.questionTypeOptions.find(o => o.value === type);
    return option?.labelKey ? this.translate.instant(option.labelKey) : '';
  }
  //#endregion
}
